import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Briefcase, TrendingUp, Filter, Loader2, Euro, Users } from "lucide-react";
import { api } from "../lib/api";

const fmtEur = (n) => `€ ${(Number(n) || 0).toLocaleString("it-IT", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
const SECTOR_LABEL = {
    efficienza_energetica: "Efficienza Energetica",
    noleggio_lungo_termine: "Noleggio LT",
    energy_quoter: "Energy Quoter",
};

export default function AdminContracts() {
    const [contracts, setContracts] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({ sector: "", agent_id: "", stage: "", date_from: "", date_to: "" });
    const [agents, setAgents] = useState([]);

    const load = () => {
        setLoading(true);
        const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
        Promise.all([
            api.get("/admin/contracts", { params }),
            api.get("/admin/contracts/summary", { params }),
        ])
            .then(([c, s]) => {
                setContracts(c.data);
                setSummary(s.data);
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        api.get("/admin/users").then(({ data }) => {
            setAgents(data.filter((u) => u.role !== "admin" && u.status === "approved"));
        });
    }, []);

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);

    const setF = (k, v) => setFilters((p) => ({ ...p, [k]: v }));
    const reset = () => setFilters({ sector: "", agent_id: "", stage: "", date_from: "", date_to: "" });

    return (
        <div className="space-y-6" data-testid="admin-contracts">
            <div>
                <div className="inline-flex items-center gap-2 bg-[#C7FF2C]/30 text-[#0C4650] px-3 py-1 rounded-full text-xs font-bold">
                    <Briefcase className="w-3 h-3" /> CONTRATTI IN LAVORAZIONE
                </div>
                <h1 className="mt-3 text-3xl sm:text-4xl font-black text-[#0C4650]">
                    Contratti & Provvigioni
                </h1>
                <p className="text-gray-600 mt-1 max-w-2xl">
                    Monitora le fasi di lavorazione dei contratti confermati e il resoconto economico delle provvigioni.
                </p>
            </div>

            {/* --- KPI cards --- */}
            {summary && (
                <div className="grid sm:grid-cols-3 gap-4">
                    <div className="card p-5" data-testid="kpi-expected">
                        <div className="text-[10px] tracking-widest font-bold text-[#898A8D]">PROVVIGIONE PREVISTA</div>
                        <div className="text-3xl font-black text-[#0C4650] mt-1">{fmtEur(summary.totals.expected)}</div>
                        <div className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                            <Euro className="w-3 h-3" /> {summary.totals.contracts_count} contratt{summary.totals.contracts_count === 1 ? "o" : "i"}
                        </div>
                    </div>
                    <div className="card p-5 bg-gradient-to-br from-[#C7FF2C]/40 to-white" data-testid="kpi-earned">
                        <div className="text-[10px] tracking-widest font-bold text-[#0C4650]">PROVVIGIONE MATURATA</div>
                        <div className="text-3xl font-black text-[#0C4650] mt-1">{fmtEur(summary.totals.earned)}</div>
                        <div className="text-xs text-gray-600 mt-1 flex items-center gap-1">
                            <TrendingUp className="w-3 h-3" /> Fatturata & liquidata
                        </div>
                    </div>
                    <div className="card p-5" data-testid="kpi-top-agent">
                        <div className="text-[10px] tracking-widest font-bold text-[#898A8D]">TOP AGENTE (MATURATO)</div>
                        {summary.by_agent[0] && summary.by_agent[0].earned > 0 ? (
                            <>
                                <div className="text-lg font-black text-[#0C4650] mt-1">{summary.by_agent[0].agent_name}</div>
                                <div className="text-xs text-gray-500 mt-1">{fmtEur(summary.by_agent[0].earned)}</div>
                            </>
                        ) : (
                            <div className="text-sm text-gray-400 mt-2">Nessun agente con provvigioni maturate</div>
                        )}
                    </div>
                </div>
            )}

            {/* --- Filtri --- */}
            <div className="card p-4">
                <div className="flex items-center gap-2 mb-3 text-sm font-bold text-[#0C4650]">
                    <Filter className="w-4 h-4" /> Filtri
                </div>
                <div className="grid sm:grid-cols-5 gap-2">
                    <select value={filters.sector} onChange={(e) => setF("sector", e.target.value)}
                            className="text-sm px-3 py-2 border-2 border-gray-200 rounded-lg" data-testid="filter-sector">
                        <option value="">Tutti i settori</option>
                        {Object.entries(SECTOR_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                    <select value={filters.agent_id} onChange={(e) => setF("agent_id", e.target.value)}
                            className="text-sm px-3 py-2 border-2 border-gray-200 rounded-lg" data-testid="filter-agent">
                        <option value="">Tutti gli agenti</option>
                        {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                    </select>
                    <input type="date" value={filters.date_from} onChange={(e) => setF("date_from", e.target.value)}
                           className="text-sm px-3 py-2 border-2 border-gray-200 rounded-lg" data-testid="filter-from" />
                    <input type="date" value={filters.date_to} onChange={(e) => setF("date_to", e.target.value)}
                           className="text-sm px-3 py-2 border-2 border-gray-200 rounded-lg" data-testid="filter-to" />
                    <button onClick={reset} className="text-sm font-bold text-[#0C4650] px-3 py-2 rounded-lg hover:bg-gray-100"
                            data-testid="filter-reset">Reset</button>
                </div>
            </div>

            {/* --- Ranking agenti --- */}
            {summary && summary.by_agent.length > 0 && (
                <div className="card p-5">
                    <div className="flex items-center gap-2 mb-3">
                        <Users className="w-4 h-4 text-[#0C4650]" />
                        <h3 className="font-black text-[#0C4650]">Classifica agenti</h3>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-[10px] tracking-widest text-[#898A8D] border-b">
                                    <th className="text-left py-2">AGENTE</th>
                                    <th className="text-right py-2">CONTRATTI</th>
                                    <th className="text-right py-2">PREVISTA</th>
                                    <th className="text-right py-2">MATURATA</th>
                                </tr>
                            </thead>
                            <tbody>
                                {summary.by_agent.slice(0, 10).map((a) => (
                                    <tr key={a.agent_id} className="border-b border-gray-50" data-testid={`agent-row-${a.agent_id}`}>
                                        <td className="py-2 font-bold text-[#0C4650]">{a.agent_name}</td>
                                        <td className="text-right py-2">{a.count}</td>
                                        <td className="text-right py-2 text-gray-600">{fmtEur(a.expected)}</td>
                                        <td className="text-right py-2 font-black text-[#0C4650]">{fmtEur(a.earned)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* --- Contratti list --- */}
            {loading ? (
                <div className="card p-10 flex items-center justify-center">
                    <Loader2 className="w-6 h-6 animate-spin text-[#0C4650]" />
                </div>
            ) : contracts.length === 0 ? (
                <div className="card p-10 text-center text-gray-500" data-testid="no-contracts">
                    Nessun contratto per i filtri selezionati.
                </div>
            ) : (
                <div className="space-y-3">
                    {contracts.map((c) => (
                        <Link key={c.id} to={`/contratti/${c.id}`}
                              className="card p-4 flex items-center justify-between hover:border-[#0C4650] transition-all block"
                              data-testid={`contract-row-${c.id}`}>
                            <div className="flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-black text-[#0C4650]">{c.client_name || "—"}</span>
                                    <span className="text-[10px] tracking-widest font-bold text-white bg-[#0C4650] px-2 py-0.5 rounded-full">
                                        {SECTOR_LABEL[c.sector] || c.sector}
                                    </span>
                                </div>
                                <div className="text-xs text-gray-500 mt-1">
                                    Fase: <b className="text-[#0C4650]">{c.current_stage_label}</b> · Creato: {(c.created_at || "").slice(0, 10)}
                                </div>
                            </div>
                            <div className="text-right shrink-0 ml-3">
                                <div className="text-[10px] text-gray-400">PROVVIGIONE</div>
                                <div className="font-black text-[#0C4650]">
                                    {fmtEur(c.commission_amount ?? c.commission_expected ?? 0)}
                                </div>
                                {c.commission_paid_at && (
                                    <div className="text-[10px] font-bold text-emerald-600">LIQUIDATA</div>
                                )}
                            </div>
                        </Link>
                    ))}
                </div>
            )}
        </div>
    );
}
